/**
 * useBadgeAutoUnlock — dispara tentativas de desbloqueio de badge via CF.
 *
 * A validação da condição é feita server-side na callable `unlockBadge`.
 * O cliente apenas lista as badges que ainda não foram desbloqueadas e
 * tenta uma por uma; a CF rejeita silenciosamente se a condição não foi atingida.
 *
 * ⚠️ **Aqui morava o unlock silencioso.** Até 03/08 este hook fazia
 * `.catch(() => {})` e jogava fora o `{unlocked}` que a callable devolve — todo
 * ganho por ofensiva, moedas, temas ou releitura acontecia sem o usuário saber.
 * O `catch` continua (a CF rejeita de propósito em corrida de stats, e isso não
 * é erro), mas o `true` agora vira fila: quem celebra é o `BadgeUnlockWatcher`.
 *
 * ## 🔴 O gatilho é a ELEGIBILIDADE, não o delta de um contador (BUG-219)
 *
 * Até 09/09 este hook reavaliava as badges apenas quando **aumentava** um de
 * quatro contadores escritos à mão (`totalBooksRead`, `wallet.coins`,
 * `totalReviews`, `totalFocusMin`). Duas consequências, as duas medidas no
 * relato de um usuário real (*"algumas das minhas conquistas continuam
 * bloqueadas, mesmo o sistema já tendo mostrado que as consegui"*):
 *
 *  1. **Páginas e ofensiva não estavam na lista.** `mil_paginas` e
 *     `ofensiva_7` — as duas do relato — só eram pedidas de CARONA num dos
 *     quatro. A lista já havia crescido uma vez, em 31/08, quando as badges de
 *     resenha e de foco ficaram presas pelo mesmo motivo: é dívida de classe,
 *     porque contador novo em `user_stats` exige alguém lembrar deste arquivo.
 *  2. **A primeira avaliação era descartada** (`hasInitializedRef`), e `profile`
 *     e `stats` chegam por listeners independentes. Com os stats chegando
 *     primeiro, a inicialização gravava os valores REAIS, nada "aumentava" e
 *     **nenhuma badge pendente era pedida naquela sessão** — quem recebia a
 *     conquista dependia de qual snapshot ganhou a corrida.
 *
 * Hoje o gatilho é a pergunta que interessa: *"existe badge elegível que eu
 * ainda não pedi?"*. Ela se auto-sincroniza com o catálogo — condição nova
 * passa a disparar sem ninguém alterar este arquivo — e dispensa o descarte da
 * primeira avaliação, porque sem badge pendente a lista sai vazia e **nada é
 * chamado**. O custo no caso normal é uma passada por `BADGE_RULES`, pura e em
 * memória.
 *
 * ⚠️ Uma tentativa por badge por MOUNT. É o que impede o laço quando a CF
 * recusa (ela recusa de propósito em corrida de stats, e o client continuaria
 * achando a badge elegível a cada snapshot). A recusa transitória é resolvida
 * na abertura seguinte do app — que antes deste conserto era justamente o
 * caminho que não funcionava.
 */

import { useEffect, useRef } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../../../hooks/useAuth";
import { useUserStats } from "../../../hooks/useUserStats";
import { getEligibleBadgeIds } from "../services/gamificationService";
import { enqueueBadgeUnlocks } from "../utils/badgeUnlockQueue";

const FUNCTIONS_REGION = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION ?? "us-central1";

export function useBadgeAutoUnlock() {
  const { user, profile } = useAuth();
  const { stats } = useUserStats(user?.uid);

  /**
   * Ids já pedidos ao servidor nesta sessão, por conta.
   *
   * Guardado junto do uid porque trocar de conta tem de zerar a memória: o
   * pendente de quem saiu não diz nada sobre quem entrou. `useRef` e não estado
   * — mudar isto não pode provocar render.
   */
  const attemptedRef = useRef<{ uid: string | null; ids: Set<string> }>({
    uid: null,
    ids: new Set(),
  });

  useEffect(() => {
    if (!profile || !user) return;

    if (attemptedRef.current.uid !== user.uid) {
      attemptedRef.current = { uid: user.uid, ids: new Set() };
    }
    const attempted = attemptedRef.current.ids;

    // Pre-check client-side: só chama a CF para badges com condição já batida.
    // CF revalida server-side (autoridade), mas evitamos N requests "esperançosos".
    const pending = getEligibleBadgeIds(stats, profile, profile.badges ?? []).filter(
      (id) => !attempted.has(id),
    );
    if (pending.length === 0) return;

    // Marcadas ANTES da chamada: dois snapshots podem cair no mesmo tick, e
    // duas rodadas concorrentes pediriam a mesma badge duas vezes — a callable
    // é idempotente, mas a segunda resposta viria com `unlocked: false` e a
    // celebração se perderia entre as duas.
    for (const id of pending) attempted.add(id);

    const functions = getFunctions(getApp(), FUNCTIONS_REGION);
    const unlockBadge = httpsCallable<{ badgeId: string }, { unlocked: boolean }>(
      functions,
      "unlockBadge",
    );

    // Enfileira em UM lote, e não uma chamada por resposta: quando várias caem
    // juntas (import de estante), o `enqueue` por badge abriria o cartão na
    // primeira e as outras virariam um segundo cartão em seguida. O
    // `Promise.allSettled` espera o tick inteiro e a fila recebe o conjunto,
    // que é o que decide entre sequência (≤3) e resumo (≥4).
    void Promise.allSettled(
      pending.map((badgeId) =>
        unlockBadge({ badgeId }).then((res) =>
          // `unlocked: false` = já tinha a badge. Só o `true` é ganho novo.
          res.data?.unlocked ? badgeId : null,
        ),
      ),
    ).then((results) => {
      const unlocked = results
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        // A CF rejeita com `failed-precondition` quando a condição não bateu —
        // corrida de stats, não erro. Cai fora aqui, em silêncio, como antes.
        .filter((id): id is string => !!id);

      if (unlocked.length > 0) enqueueBadgeUnlocks(unlocked);
    });
    // ⚠️ Sem `eslint-disable` aqui, diferente da versão anterior: as
    // dependências abaixo são as três de verdade, e a memória de tentativas é
    // um ref. O disable herdado passou a ser inútil quando o gatilho deixou de
    // ler cinco refs de contador.
  }, [profile, user, stats]);
}
